import React, { useEffect, useState, useRef } from "react";
import {
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import "./App.css";
import { getAllPlaylist, getPlaylist } from "./service/featured";
import { getPlaylistBookmarks, togglePlaylistBookmark } from "./service/auth";
import { getPlaylistUrl } from "./utils/urlHelpers";
import Header from "./components/Header";
import Footer from "./components/Footer";

const getPlaylistBookmarkedValue = (playlist) => {
  return Boolean(playlist?.is_bookmarked || playlist?.isBookmarked || playlist?.bookmarked);
};

const getBookmarkedFromResponse = (response, fallback) => {
  const value = response.data?.data?.bookmarked;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return fallback;
};

const ViewPlaylists = () => {
  const [categories, setCategories] = useState([]);
  const [showPlaylistMenu, setShowPlaylistMenu] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [playlistSearchTerm, setPlaylistSearchTerm] = useState("");
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [bookmarkingPlaylistIds, setBookmarkingPlaylistIds] = useState(new Set());
  const [showStarAnimation, setShowStarAnimation] = useState(false);
  const [animationStyle, setAnimationStyle] = useState({});
  const starBtnRefs = useRef({});

  useEffect(() => {
    Promise.all([
      getAllPlaylist(),
      getPlaylistBookmarks().catch(() => null),
    ]).then(([playlistResponse, bookmarkResponse]) => {
      const playlists = bookmarkResponse?.data?.data?.bookmarks || bookmarkResponse?.data?.data?.playlists || [];
      const bookmarkedIds = new Set(playlists.map((playlist) => playlist.id));
      const categoriesWithBookmarkState = playlistResponse.data.data.categories.map((category) => ({
        ...category,
        playlists: category.playlists.map((playlist) => ({
          ...playlist,
          is_bookmarked: bookmarkedIds.has(playlist.id) || getPlaylistBookmarkedValue(playlist),
        })),
      }));

      setCategories(categoriesWithBookmarkState);
    }).catch((error) => {
      console.error("Failed to fetch playlists:", error);
    });
  }, []);

  const updatePlaylistBookmarkState = (playlistId, isBookmarked) => {
    setCategories((prevCategories) =>
      prevCategories.map((category) => ({
        ...category,
        playlists: category.playlists.map((playlist) =>
          playlist.id === playlistId
            ? { ...playlist, is_bookmarked: isBookmarked }
            : playlist
        ),
      }))
    );

    setSelectedPlaylist((prevPlaylist) =>
      prevPlaylist?.id === playlistId
        ? { ...prevPlaylist, is_bookmarked: isBookmarked }
        : prevPlaylist
    );
  };

  const handlePlaylistClick = (playlist) => {
    window.location.href = getPlaylistUrl(playlist);
  }

  const handleVideoSelect = (videoId) => {
    const video = playlistVideos.find(v => v.id === videoId);
    // Navigate to playlist page with video order as query parameter
    window.location.href = getPlaylistUrl(selectedPlaylist, { video: video?.order || 0 });
  }

  const handlePlaylistBookmarkClick = async (playlist, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!playlist?.id || bookmarkingPlaylistIds.has(playlist.id)) return;

    const wasBookmarked = getPlaylistBookmarkedValue(playlist);
    const nextBookmarked = !wasBookmarked;

    setBookmarkingPlaylistIds((prev) => new Set(prev).add(playlist.id));
    updatePlaylistBookmarkState(playlist.id, nextBookmarked);

    try {
      const response = await togglePlaylistBookmark(playlist.id);
      const bookmarked = getBookmarkedFromResponse(response, nextBookmarked);
      updatePlaylistBookmarkState(playlist.id, bookmarked);

      if (bookmarked) {
        // Trigger flying star animation
        const starBtn = starBtnRefs.current[playlist.id];
        if (starBtn) {
          const rect = starBtn.getBoundingClientRect();

          // Calculate animation start position (relative to viewport)
          setAnimationStyle({
            '--start-x': `${rect.left + rect.width / 2}px`,
            '--start-y': `${rect.top + rect.height / 2}px`,
          });

          // Trigger animation
          setShowStarAnimation(true);

          // Trigger emoji bounce after 0.2s (when star reaches the emoji)
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('starAddedToLibrary'));
          }, 200);

          // Remove animation after it completes
          setTimeout(() => {
            setShowStarAnimation(false);
          }, 800);
        }
      }
    } catch (error) {
      console.error("Failed to toggle playlist bookmark:", error);
      updatePlaylistBookmarkState(playlist.id, wasBookmarked);
    } finally {
      setBookmarkingPlaylistIds((prev) => {
        const next = new Set(prev);
        next.delete(playlist.id);
        return next;
      });
    }
  };
  
  return (
    <div style={{ backgroundColor: "black", padding: "20px" }}>
      <Header />
      {categories.length > 0 ? (
        <div>
          {categories.map((record, idx) => {
            return (
              <div key={idx} className="category-section">
                <h2 className="category-title">{record.name}</h2>
                <div className="category-carousel-container">
                  <div className="category-carousel">
                    {record.playlists.map((playlist, idy) => {
                      return (
                        <div
                          key={idy}
                          className="category-playlist-item"
                          onClick={() => handlePlaylistClick(playlist)}
                        >
                          <button
                            ref={(el) => starBtnRefs.current[playlist.id] = el}
                            className={`playlist-bookmark-btn ${getPlaylistBookmarkedValue(playlist) ? "active" : ""}`}
                            onClick={(e) => handlePlaylistBookmarkClick(playlist, e)}
                            disabled={bookmarkingPlaylistIds.has(playlist.id)}
                            title={getPlaylistBookmarkedValue(playlist) ? "Remove playlist from library" : "Save playlist to library"}
                            aria-label={getPlaylistBookmarkedValue(playlist) ? "Remove playlist from library" : "Save playlist to library"}
                          >
                            <svg viewBox="0 0 24 24" fill={getPlaylistBookmarkedValue(playlist) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                          </button>
                          <img src={playlist.image_url} alt={playlist.name} />
                          <div className="category-playlist-info">
                            <h6 className="font-semibold">{playlist.name}</h6>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="loading-container">
          <Spinner color="warning">Loading....</Spinner>
        </div>
      )}
      {/* <div className="img-con">
        <a href="https://app.gogopix.co/gogopixtv">
          <img
            className="imgBtn"
            style={{ marginTop: "45px" }}
            src={stoIcns}
            width="30%"
            alt="get the app logo"
          />
        </a>
      </div> */}
      <Footer />

      {/* Playlist Menu Popup */}
      <Modal
        isOpen={showPlaylistMenu}
        toggle={() => {
          setShowPlaylistMenu(false);
          setPlaylistSearchTerm("");
        }}
        className="playlist-menu-popup"
        size="lg"
      >
        <ModalHeader toggle={() => {
          setShowPlaylistMenu(false);
          setPlaylistSearchTerm("");
        }}>
          {selectedPlaylist?.name || 'Playlist'}
        </ModalHeader>
        <ModalBody className="playlist-menu-popup-body">
          {/* Playlist Artwork */}
          {selectedPlaylist?.image_url && (
            <div className="playlist-menu-artwork">
              {selectedPlaylist && (
                <button
                  className={`playlist-bookmark-btn playlist-bookmark-btn--modal ${getPlaylistBookmarkedValue(selectedPlaylist) ? "active" : ""}`}
                  onClick={(e) => handlePlaylistBookmarkClick(selectedPlaylist, e)}
                  disabled={bookmarkingPlaylistIds.has(selectedPlaylist.id)}
                  title={getPlaylistBookmarkedValue(selectedPlaylist) ? "Remove playlist from library" : "Save playlist to library"}
                  aria-label={getPlaylistBookmarkedValue(selectedPlaylist) ? "Remove playlist from library" : "Save playlist to library"}
                >
                  <svg viewBox="0 0 24 24" fill={getPlaylistBookmarkedValue(selectedPlaylist) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </button>
              )}
              <img
                src={selectedPlaylist.image_url}
                alt={selectedPlaylist.name}
                className="playlist-menu-artwork-image"
              />
            </div>
          )}

          {/* Search Input */}
          <div className="playlist-menu-search">
            <input
              type="text"
              className="playlist-menu-search-input"
              placeholder="Search videos by artist or title..."
              value={playlistSearchTerm}
              onChange={(e) => setPlaylistSearchTerm(e.target.value)}
            />
          </div>

          {/* Vertical Video List */}
          <div className="playlist-menu-videos">
            {loadingVideos ? (
              <div className="playlist-loading">
                <Spinner color="warning">Loading....</Spinner>
              </div>
            ) : playlistVideos.length > 0 ? (
              playlistVideos
                .filter((record) => {
                  if (!playlistSearchTerm) return true;
                  const searchLower = playlistSearchTerm.toLowerCase();
                  return (
                    record.title.toLowerCase().includes(searchLower) ||
                    record.artist.toLowerCase().includes(searchLower)
                  );
                })
                .map((record) => (
                  <div
                    key={record.id}
                    className="playlist-menu-video-item"
                    onClick={() => handleVideoSelect(record.id)}
                  >
                    <img src={record.thumbnail_url} alt={record.title} />
                    <div className="playlist-menu-video-info">
                      <h6 className="font-semibold">{record.title}</h6>
                      <p>{record.artist}</p>
                    </div>
                  </div>
                ))
            ) : (
              <div className="no-results">
                <p>No videos found</p>
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Flying Star Animation */}
      {showStarAnimation && (
        <div className="flying-star-container" style={animationStyle}>
          <div className="flying-star">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          {/* Sparkle particles */}
          <div className="sparkle sparkle-1"></div>
          <div className="sparkle sparkle-2"></div>
          <div className="sparkle sparkle-3"></div>
          <div className="sparkle sparkle-4"></div>
          <div className="sparkle sparkle-5"></div>
        </div>
      )}
    </div>
  );
};

export default ViewPlaylists;
